// Adding or editing one competency or induction.
//
// Two behaviours here are ported deliberately from Zanetto Tools, and both are
// about what happens when something goes half-right:
//
//   1. The RECORD saves first, then the file uploads. If the upload fails the
//      record is still there and the message says to use the folder's upload
//      box to try again — the alternative is throwing away a correctly-entered
//      licence because the phone lost signal mid-scan.
//
//   2. The expiry date is PRE-FILLED from the code's validity and stays
//      editable — over an empty box or over its own previous suggestion,
//      never over a hand-typed date. A ticket issued under an older validity
//      keeps the date printed on the card, so this is a good first guess and
//      never a computed answer — the server does not do this arithmetic at
//      all.
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api';
import { uploadAttachment } from '../../documentsApi';
import {
  codeOptions,
  inductionOptions,
  people,
  prefillExpiry,
  type CompetencyCode,
  type InductionCode,
  type PersonRecord,
} from '../../peopleApi';
import type { PersonSection } from '../../lib/personSections';
import { Modal, SingleFileDrop } from '../ui';
import { Banner, Field } from '../../ui';

export function RecordModal({
  resourceId,
  section,
  codes,
  inductions,
  record,
  onClose,
  onSaved,
}: {
  resourceId: string;
  section: PersonSection;
  codes: CompetencyCode[];
  inductions: InductionCode[];
  /** Null when adding. */
  record: PersonRecord | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const isCompetency = section.recordCategory === 'competency';

  // One id, whichever list this dialog is for (round 37 made inductions
  // code-backed too, so both categories pick from a dropdown).
  const [codeId, setCodeId] = useState(
    (isCompetency ? record?.codeId : record?.inductionCodeId) ?? ''
  );
  const [recordNumber, setRecordNumber] = useState(record?.recordNumber ?? '');
  const [issueDate, setIssueDate] = useState(record?.issueDate ?? '');
  const [expiryDate, setExpiryDate] = useState(record?.expiresOn ?? '');
  const [notes, setNotes] = useState(record?.notes ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const options = isCompetency
    ? codeOptions(codes, record?.codeId ?? null)
    : inductionOptions(inductions, record?.inductionCodeId ?? null);

  /**
   * Fills the expiry from the code's validity — over an empty box, or over
   * its OWN previous suggestion (so correcting the issue date corrects the
   * suggestion with it). Never over a date somebody read off the card and
   * typed in: the Expires field's own onChange clears `suggested`, and a
   * hand-typed value is then left alone for good.
   */
  const suggested = useRef<string | null>(null);
  function prefill(nextCodeId: string, nextIssue: string) {
    if (expiryDate !== '' && expiryDate !== suggested.current) return;
    const code = (isCompetency ? codes : inductions).find((c) => c.id === nextCodeId);
    if (!code || !nextIssue) return;
    const next = prefillExpiry(nextIssue, code.validityMonths);
    if (next) {
      suggested.current = next;
      setExpiryDate(next);
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});

    const body = {
      category: section.recordCategory!,
      ...(isCompetency
        ? { competencyCodeId: codeId || null }
        : { inductionCodeId: codeId || null }),
      recordNumber: recordNumber.trim() || null,
      issueDate: issueDate || null,
      expiryDate: expiryDate || null,
      notes: notes.trim() || null,
    };

    try {
      const saved = record
        ? await people.updateRecord(resourceId, record.id, body)
        : await people.addRecord(resourceId, body);

      // The record is safe from here on. A failed upload below costs the file
      // and says so; it never costs the record.
      if (file) {
        try {
          await uploadAttachment({
            kind: 'resource',
            ownerId: resourceId,
            file,
            recordId: saved.record.id,
          });
        } catch (err) {
          onSaved(
            `${saved.record.title} was saved, but the file did not upload; ` +
              `${err instanceof ApiError ? err.message : 'the upload failed'}. ` +
              'Add it from the folder’s upload box to try again.'
          );
          return;
        }
      }

      onSaved(record ? 'Saved.' : `${saved.record.title} added.`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={record ? `Edit ${record.title}` : section.addLabel!}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="record-form" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="record-form" onSubmit={save} noValidate>
        {error && <Banner kind="error">{error}</Banner>}

        {options.length === 0 ? (
          /* Blue, not amber: this is a fact about where the codes live, not
             a warning that something is wrong (the semantic set — spec
             brand rules). */
          <p className="banner-info mb-4">
            {isCompetency ? (
              <>
                No competency codes yet.{' '}
                <Link to="/app/settings/competency-codes">Add them in Settings</Link> first; a
                competency takes its name from its code.
              </>
            ) : (
              <>
                No inductions on your list yet.{' '}
                <Link to="/app/settings/induction-codes">Add them in Settings</Link> first; an
                induction takes its name from the list.
              </>
            )}
          </p>
        ) : (
          <label htmlFor="record-code" className="block">
            {/* The add-a-code shortcut sits inline with the label (round 37,
                item 9). A plain Link: navigating unmounts the modal, and the
                small state loss beats a second, nested implementation of the
                codes page. Clicking a link inside a label does not activate
                the labelled control. */}
            <span className="flex items-baseline justify-between gap-2">
              <span>{isCompetency ? 'Competency' : 'Induction'}</span>
              <Link
                to={
                  isCompetency
                    ? '/app/settings/competency-codes'
                    : '/app/settings/induction-codes'
                }
                className="row-action"
              >
                {isCompetency ? 'Add competency' : 'Add induction'}
              </Link>
            </span>
            <select
              id="record-code"
              className="form-input"
              value={codeId}
              onChange={(e) => {
                setCodeId(e.target.value);
                prefill(e.target.value, issueDate);
              }}
            >
              <option value="">
                {isCompetency ? 'Select a licence, ticket or course…' : 'Select an induction…'}
              </option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isCompetency
              ? fields.competencyCodeId && (
                  <span className="field-error">{fields.competencyCodeId}</span>
                )
              : fields.inductionCodeId && (
                  <span className="field-error">{fields.inductionCodeId}</span>
                )}
          </label>
        )}

        <Field
          label="Number"
          name="recordNumber"
          value={recordNumber}
          onChange={setRecordNumber}
          error={fields.recordNumber}
          required={false}
          hint="Licence or certificate number, as printed."
          autoCapitalize="none"
          spellCheck={false}
        />

        {/* `pair`: the two dates stay side by side on a phone instead of
            wrapping into a cramped stack (legacy.css has the argument). */}
        <div className="row pair">
          <Field
            label="Issued"
            name="issueDate"
            type="date"
            value={issueDate}
            onChange={(value) => {
              setIssueDate(value);
              prefill(codeId, value);
            }}
            error={fields.issueDate}
            required={false}
          />
          <Field
            label="Expires"
            name="expiryDate"
            type="date"
            value={expiryDate}
            onChange={(value) => {
              // A hand-edit ends the suggestion's claim on this box.
              suggested.current = null;
              setExpiryDate(value);
            }}
            error={fields.expiryDate}
            required={false}
          />
        </div>
        {/* The hint sits UNDER THE ROW, not on the Expires field.
            `.row` is `items-end`, and `Field` renders a hint below its input —
            so a hint on one of two side-by-side fields makes that field taller
            and pushes its input up, leaving the two date boxes visibly out of
            line. (An ERROR does not do this: errors render on the label line
            for exactly this reason.) The sentence describes the pair anyway. */}
        <p className="hint -mt-1">Leave the expiry blank if it doesn’t expire.</p>

        <label htmlFor="record-notes" className="block">
          Notes
          <textarea
            id="record-notes"
            className="form-input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        {/* On create AND edit (round 37, item 6 — reversing round 27's
            create-only gate). The old objection was that re-picking a file on
            an edit is ambiguous between "replace the scan" and "add a second
            one"; the record's existing scans are listed right on the row with
            their own unlink controls, so "adds another" is now legible, and a
            scan forgotten at creation was otherwise unattachable from the
            dialog forever. save() always uploaded after save — only the UI
            was gated. */}
        <div>
          <span className="mb-1 block">Scan or photo</span>
          {/* Round 45: a drop zone as well as a browse, through the shared
              FileDrop (its hidden input keeps round 33's rule). */}
          <SingleFileDrop
            id="record-file"
            file={file}
            onChange={setFile}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic"
            typeLabel="PDF or image"
            onReject={setError}
          />
          <span className="hint">
            {record
              ? 'Optional: adds another scan. The ones already attached are listed on the record.'
              : 'Optional: you can attach one later instead. The record holds the expiry date, so the file does not need its own.'}
          </span>
        </div>
      </form>
    </Modal>
  );
}
